import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/shared/model/authContext";
import { useMyTenants } from "@/entities/session/model/useSessionTenants";
import { useTenantRouting } from "@/shared/lib/router/tenantPath";
import { reloadAt } from "@/shared/lib/router/reload";
import { getApiErrorMessage } from "@/shared/lib/errorMessage";
import { useToast } from "@/shared/ui/toast/ToastContext";
import { pathUnderTenant, resolveTenantSlugAction } from "@/features/tenant-session/lib/tenantSlugAction";
import { useSwitchTenant } from "./useSwitchTenant";

/**
 * A URL manda na organização: o slug de outra organização da pessoa troca a sessão e, sem
 * slug, ganha o da atual. A base do router só muda recarregando a página (ver main.tsx).
 */
export function useTenantSlugSync(): void {
    const { t } = useTranslation("common");
    const toast = useToast();
    const { isAuthenticated, isSupportSession } = useAuth();
    const { slug: urlSlug, isRoutableSlug } = useTenantRouting();
    // Numa sessão de suporte a troca é recusada pelo backend.
    const { data: tenants } = useMyTenants(isAuthenticated && !isSupportSession);
    const { mutate: switchTenant } = useSwitchTenant();
    // Toda ação recarrega a página; o StrictMode e um refetch não podem dispará-la duas vezes.
    const dispatched = useRef(false);

    useEffect(() => {
        if (!tenants || dispatched.current) return;
        const action = resolveTenantSlugAction({ urlSlug, pathname: window.location.pathname, tenants, isRoutableSlug });
        if (action.type === "none") return;
        dispatched.current = true;
        if (action.type === "redirect") {
            reloadAt(pathUnderTenant(action.slug, window.location, urlSlug), { replace: true });
            return;
        }

        // O endereço já é o da organização de destino: só falta a sessão dela.
        switchTenant(
            { tenantId: action.tenantId, destination: window.location.href },
            {
                onError: (error) => {
                    console.error("[tenant-session] Troca de organização pela URL falhou:", error);
                    toast.error(getApiErrorMessage(error, t("tenantSwitcher.error")));
                    if (action.fallbackSlug) reloadAt(pathUnderTenant(action.fallbackSlug, window.location, urlSlug), { replace: true });
                },
            },
        );
    }, [tenants, urlSlug, isRoutableSlug, switchTenant, toast, t]);
}
